import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getPageTracker } from '../../utils/pageTracking';
import { useAnalyticsContext } from '../../contexts/AnalyticsContext';

/**
 * PageTracker component that automatically tracks pageviews with enhanced properties
 * following PostHog's official best practices for React SPAs
 */
export const PageTracker = () => {
  const location = useLocation();
  const { trackPageView, isInitialized } = useAnalyticsContext();

  // Map paths to descriptive page names following PostHog's object_adjective pattern
  const getPageInfo = (pathname: string) => {
    const pageMap: Record<string, { name: string; category: string; feature_area?: string }> = {
      '/': { 
        name: 'Homepage', 
        category: 'marketing' 
      },
      '/login': { 
        name: 'Login Page', 
        category: 'authentication' 
      },
      '/signup': { 
        name: 'Signup Page', 
        category: 'authentication' 
      },
      '/forgot-password': { 
        name: 'Password Reset Page', 
        category: 'authentication' 
      },
      '/verify-email': { 
        name: 'Email Verification Page', 
        category: 'authentication' 
      },
      '/complete-profile': { 
        name: 'Profile Completion Page', 
        category: 'onboarding' 
      },
      '/dashboard': { 
        name: 'Dashboard Home', 
        category: 'dashboard', 
        feature_area: 'overview' 
      },
      '/chat': { 
        name: 'Chat Interface', 
        category: 'learning', 
        feature_area: 'ai_chat' 
      },
      '/documents': { 
        name: 'Document Library', 
        category: 'learning', 
        feature_area: 'document_management' 
      },
      '/courses': { 
        name: 'Courses Page', 
        category: 'learning', 
        feature_area: 'course_catalog' 
      },
      '/profile': { 
        name: 'User Profile', 
        category: 'account', 
        feature_area: 'profile_management' 
      },
      '/billing': { 
        name: 'Billing Page', 
        category: 'account', 
        feature_area: 'subscription_management' 
      },
      '/pricing': { 
        name: 'Pricing Page', 
        category: 'marketing', 
        feature_area: 'conversion' 
      },
      '/skill-graph-test': { 
        name: 'Skill Graph Test', 
        category: 'development', 
        feature_area: 'skill_visualization' 
      }
    };

    // Handle dynamic routes
    if (pathname.startsWith('/chat/')) {
      return { 
        name: 'Chat Conversation', 
        category: 'learning', 
        feature_area: 'ai_chat' 
      };
    }
    
    if (pathname.startsWith('/documents/')) {
      return { 
        name: 'Document Detail', 
        category: 'learning', 
        feature_area: 'document_management' 
      };
    }

    // Default for unmapped pages
    return pageMap[pathname] || { 
      name: pathname.slice(1) || 'Unknown Page', 
      category: 'other' 
    };
  };

  useEffect(() => {
    if (!isInitialized) return;

    const pageInfo = getPageInfo(location.pathname);
    
    // Start page tracking with our enhanced system
    const pageTracker = getPageTracker();
    if (pageTracker) {
      pageTracker.startPageTracking(location.pathname, pageInfo.name);
    }
    
    // Track the page view with enhanced properties
    trackPageView(pageInfo.name, {
      page_category: pageInfo.category,
      page_search: location.search,
      page_hash: location.hash,
      ...(pageInfo.feature_area && { feature_area: pageInfo.feature_area }),
      // Add timestamp for custom analysis
      page_view_timestamp: new Date().toISOString()
    });

    // Cleanup function to stop tracking when leaving page
    return () => {
      if (pageTracker) {
        pageTracker.stopPageTracking();
      }
    };
  }, [location, isInitialized, trackPageView]);

  // This component doesn't render anything
  return null;
};

export default PageTracker;